/**
 * SuiteCRM is a customer relationship management program developed by SuiteCRM Ltd.
 * Copyright (C) 2021 SuiteCRM Ltd.
 *
 * This program is free software; you can redistribute it and/or modify it under
 * the terms of the GNU Affero General Public License version 3 as published by the
 * Free Software Foundation with the addition of the following permission added
 * to Section 15 as permitted in Section 7(a): FOR ANY PART OF THE COVERED WORK
 * IN WHICH THE COPYRIGHT IS OWNED BY SUITECRM, SUITECRM DISCLAIMS THE
 * WARRANTY OF NON INFRINGEMENT OF THIRD PARTY RIGHTS.
 *
 * This program is distributed in the hope that it will be useful, but WITHOUT
 * ANY WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS
 * FOR A PARTICULAR PURPOSE. See the GNU Affero General Public License for more
 * details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <http://www.gnu.org/licenses/>.
 *
 * In accordance with Section 7(b) of the GNU Affero General Public License
 * version 3, these Appropriate Legal Notices must retain the display of the
 * "Supercharged by SuiteCRM" logo. If the display of the logos is not reasonably
 * feasible for technical reasons, the Appropriate Legal Notices must display
 * the words "Supercharged by SuiteCRM".
 */

import {Injectable} from '@angular/core';
import {BehaviorSubject, Observable, Subscription} from 'rxjs';
import {debounceTime} from 'rxjs/operators';
import {SystemConfigStore} from '../../../store/system-config/system-config.store';

@Injectable()
export class LoadingBuffer {

    loading$: Observable<boolean>;

    protected loadingStore: BehaviorSubject<boolean>;
    protected loadingBufferStore: BehaviorSubject<boolean>;
    protected loadingBufferStore$: Observable<boolean>;
    protected subs: Subscription[] = [];
    protected buffered = false;

    constructor(
        protected config: SystemConfigStore,
        protected delayConfigKey: string = 'loading_display_delay',
        protected initialStatus: boolean = false
    ) {

        this.loadingStore = new BehaviorSubject<boolean>(initialStatus);
        this.loadingBufferStore = new BehaviorSubject<boolean>(initialStatus);

        this.loading$ = this.loadingStore.asObservable();

        const uiSettings = config.getConfigValue('ui') ?? {};
        const delay = parseInt(uiSettings[delayConfigKey] ?? '') ?? 1500;

        this.loadingBufferStore$ = this.loadingBufferStore.asObservable().pipe(debounceTime(delay));
        this.subs.push(this.loadingBufferStore$.subscribe((loading) => {
            if (!this.buffered) {
                return;
            }
            this.loadingStore.next(loading);
        }));
    }

    /**
     * Update loading status
     *
     * @param {boolean} loading status to set
     */
    public updateLoading(loading: boolean): void {

        if (loading === true) {
            this.buffered = true;
            this.loadingBufferStore.next(loading);
            return;
        }

        this.buffered = false;
        this.loadingStore.next(loading);
        this.loadingBufferStore.next(loading);
    }
}
